import { Host, Hosts, HostState } from './types';
import { combineReducers } from 'redux';
import { createReducer } from 'typesafe-actions';
import { fetchHosts, saveHostSearchValue, resetHosts, FetchHostsSuccessPayload } from './actions';
import { isStandardPaginationResult } from '../utils';

export const initialState: HostState = {
  data: null,
  error: null,
  loading: false,
  loadingMore: false,
  fieldValue: '',
  total: null,
  offset: 0,
  query: '',
};

const loading = createReducer(initialState.loading)
  .handleAction(fetchHosts.request, (_state, action) => (action.payload.offset ?? 0) === 0)
  .handleAction(fetchHosts.success, () => false)
  .handleAction(fetchHosts.failure, () => false)
  .handleAction(resetHosts, () => false);

const loadingMore = createReducer(initialState.loadingMore)
  .handleAction(fetchHosts.request, (_state, action) => (action.payload.offset ?? 0) > 0)
  .handleAction(fetchHosts.success, () => false)
  .handleAction(fetchHosts.failure, () => false)
  .handleAction(resetHosts, () => false);

const error = createReducer(initialState.error)
  .handleAction(fetchHosts.success, () => null)
  .handleAction(fetchHosts.failure, (_state, action) => action.payload.error)
  .handleAction(resetHosts, () => null);

const extractHosts = (payload: FetchHostsSuccessPayload): Host[] | null => {
  const { hosts } = payload;
  if (!hosts || !hosts.data) {
    return null;
  }
  if (isStandardPaginationResult<Host, 'hosts'>(hosts as Hosts)) {
    return (hosts as { data: { hosts: Host[] } }).data.hosts || null;
  }
  return hosts.data as Host[];
};

const data = createReducer(initialState.data)
  .handleAction(fetchHosts.success, (state, action) => {
    const newHosts = extractHosts(action.payload);
    if (!newHosts) {
      return action.payload.params.offset === 0 ? null : state;
    }
    // If offset is 0, replace data; otherwise append
    if (action.payload.params.offset === 0) {
      return newHosts;
    }
    return state ? [...state, ...newHosts] : newHosts;
  })
  .handleAction(fetchHosts.failure, () => null)
  .handleAction(resetHosts, () => null);

const fieldValue = createReducer(initialState.fieldValue)
  .handleAction(saveHostSearchValue, (_state, action) => action.payload)
  .handleAction(resetHosts, () => '');

const total = createReducer(initialState.total)
  .handleAction(fetchHosts.success, (_state, action) => {
    const { hosts } = action.payload;
    if (!hosts || typeof hosts !== 'object') {
      return null;
    }
    if ('total' in hosts && typeof hosts.total === 'number') {
      return hosts.total;
    }
    return null;
  })
  .handleAction(resetHosts, () => null);

const offset = createReducer(initialState.offset)
  .handleAction(fetchHosts.success, (state, action) => {
    const newHosts = extractHosts(action.payload);
    const count = newHosts?.length ?? 0;
    // If offset is 0, start fresh; otherwise add to current offset
    if (action.payload.params.offset === 0) {
      return count;
    }
    return state + count;
  })
  .handleAction(resetHosts, () => 0);

const query = createReducer(initialState.query)
  .handleAction(fetchHosts.success, (_state, action) => action.payload.params.query ?? '')
  .handleAction(resetHosts, () => '');

export default combineReducers({ loading, loadingMore, error, data, fieldValue, total, offset, query });
